import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import SidebarLayout from '../../components/SidebarLayout';
import OrganisationLogo from '../../components/OrganisationLogo';
import styles from './ProfilePage.module.css';
import BillingSection from './BillingSection';
import { API_BASE_URL as API_URL } from '../../config/apiBase';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const { showError, showSuccess } = useToast();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [isEditingFullName, setIsEditingFullName] = useState(false);
  const [newFullName, setNewFullName] = useState('');

  // Tabs: state and deep-linking
  const TABS = [
    { key: 'personal', label: 'Personal Info' },
    { key: 'account', label: 'Account Details' },
    { key: 'authentication', label: 'Authentication' },
    { key: 'activity', label: 'Activity' },
    { key: 'billing', label: 'Billing' },
  ];
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('personal');
  const tabRefs = useRef({});
  const underlineRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = (params.get('tab') || '').toLowerCase();
    const valid = TABS.find(t => t.key === q)?.key || 'personal';
    setActiveTab(valid);
  }, [location.search]);

  const updateUrlTab = (tabKey) => {
    const params = new URLSearchParams(location.search);
    params.set('tab', tabKey);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  useEffect(() => {
    const el = tabRefs.current[activeTab];
    const underline = underlineRef.current;
    if (!el || !underline) return;
    const setPosition = () => {
      const { offsetLeft, offsetWidth } = el;
      underline.style.transform = `translateX(${offsetLeft}px)`;
      underline.style.width = `${offsetWidth}px`;
    };
    setPosition();
    window.addEventListener('resize', setPosition);
    return () => window.removeEventListener('resize', setPosition);
  }, [activeTab]);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch profile data');
        }

        const data = await response.json();
        setProfileData(data.data.user);

        const sectorResponse = await fetch(`${API_URL}/sectors/${data.data.user?.sectorId}`, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        });
        if (!sectorResponse.ok) {
          throw new Error('Failed to fetch sector data');
        }
        const sectorData = await sectorResponse.json();
        setProfileData(prev => ({ ...prev, sector: sectorData.data.sector }));
      } catch (error) {
        console.error('Error fetching profile data:', error);
        showError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [getAuthHeaders, showError]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUsernameUpdate = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) {
      showError('Username cannot be empty');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/auth/username`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newUsername: newUsername.trim() })
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(data.message);
        setProfileData(prev => ({ ...prev, username: data.data.user.username }));
        setIsEditingUsername(false);
      } else {
        showError(data.message);
      }
    } catch (error) {
      console.error('Error updating username:', error);
      showError('Failed to update username');
    }
  };

  const handleFullNameUpdate = async (e) => {
    e.preventDefault();
    if (!newFullName.trim()) {
      showError('Full name cannot be empty');
      return;
    }
    if (newFullName.trim().length < 2) {
      showError('Full name must be at least 2 characters long');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/auth/fullname`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newFullName: newFullName.trim() })
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(data.message);
        setProfileData(prev => ({ ...prev, fullName: data.data.user.full_name }));
        setIsEditingFullName(false);
      } else {
        showError(data.message);
      }
    } catch (error) {
      console.error('Error updating full name:', error);
      showError('Failed to update full name');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      showError('New password must be at least 6 characters long');
      return;
    }

    setUpdatingPassword(true);

    try {
      const response = await fetch(`${API_URL}/auth/password`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: profileData?.password ? passwordForm.currentPassword : undefined,
          newPassword: passwordForm.newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(data.message);
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswordForm(false);
        
        // Refresh profile data to get updated providers
        const refreshResponse = await fetch(`${API_URL}/auth/me`, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        });
        
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setProfileData(refreshData.data.user);
        }
      } else {
        showError(data.message);
      }
    } catch (error) {
      console.error('Error updating password:', error);
      showError('Failed to update password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const getPasswordStatus = () => {
    if (!profileData?.providers) return 'Unknown';
    
    if (profileData.providers.includes('local')) {
      return profileData.password ? 'Set' : 'Not set';
    }
    return 'Not set';
  };

  const getPasswordStatusColor = () => {
    const status = getPasswordStatus();
    if (status === 'Set') return styles.statusSet;
    if (status === 'Not set') return styles.statusNotSet;
    return styles.statusUnknown;
  };

  // Render skeleton loaders
  const renderSkeleton = () => {
    return (
      <div className={styles.profileCard}>
        <div className={styles.header}>
          <div className={`${styles.avatar} ${styles.skeletonAvatar}`}></div>
          <div className={styles.skeletonTitle}></div>
        </div>
        <div className={styles.content}>
          {[...Array(5)].map((_, sectionIndex) => (
            <div key={sectionIndex} className={styles.section}>
              <div className={`${styles.sectionTitle} ${styles.skeletonSectionTitle}`}></div>
              <div className={styles.infoGrid}>
                {[...Array(sectionIndex === 0 ? 4 : sectionIndex === 1 ? 6 : sectionIndex === 2 ? 3 : sectionIndex === 3 ? 2 : 4)].map((_, itemIndex) => (
                  <div key={itemIndex} className={`${styles.infoItem} ${styles.skeletonInfoItem}`}>
                    <div className={styles.skeletonLabel}></div>
                    <div className={styles.skeletonValue}></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
  
    const date = new Date(dateString);
  
    const offsetMs = 5.5 * 60 * 60 * 1000;
    const adjustedDate = new Date(date.getTime() + offsetMs);
  
    return adjustedDate.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };
  
  

  const getDisplayValue = (value, fallback = 'Not available') => {
    return value || fallback;
  };

  const getDisplayName = () => {
    return profileData?.fullName || profileData?.username || 'User';
  };

  return (
    <SidebarLayout>
        <div className={styles.container}>
          <header className={styles.pageHead}>
            <h1>Profile</h1>
            <p>Your account, organisation, and billing.</p>
          </header>
          {loading ? (
            renderSkeleton()
          ) : (
          <div className={styles.profileCard}>
        <div className={styles.header}>
          <div className={styles.avatar} aria-hidden="true">
            {getDisplayName().charAt(0).toUpperCase()}
          </div>
          <div className={styles.identity}>
            <h2 className={styles.title}>{getDisplayName()}</h2>
            <p className={styles.subtitle}>{getDisplayValue(profileData?.email, '')}</p>
          </div>
          {profileData?.role && (
            <span className={styles.badge}>{profileData.role}</span>
          )}
        </div>

        <div className={styles.content}>
          {/* Top navigation tabs */}
          <div
            role="tablist"
            aria-label="Profile sections"
            className={styles.tabBar}
          >
            {TABS.map(tab => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                aria-controls={`section-${tab.key}`}
                ref={el => (tabRefs.current[tab.key] = el)}
                onClick={() => {
                  setActiveTab(tab.key);
                  updateUrlTab(tab.key);
                }}
                className={`${styles.tabButton} ${activeTab === tab.key ? styles.tabButtonActive : ''}`}
              >
                {tab.label}
              </button>
            ))}
            <span ref={underlineRef} aria-hidden="true" className={styles.tabUnderline} />
          </div>

          {activeTab === 'personal' && (
          <div id="section-personal" role="tabpanel" aria-labelledby="tab-personal" className={styles.section}>
            <h2 className={styles.sectionTitle}>Personal Information</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label className={styles.label}>Full Name</label>
                {isEditingFullName ? (
                  <form onSubmit={handleFullNameUpdate} className={styles.usernameForm}>
                    <input
                      type="text"
                      value={newFullName}
                      onChange={(e) => setNewFullName(e.target.value)}
                      className={styles.usernameInput}
                      autoFocus
                      placeholder="Enter your full name"
                    />
                    <div className={styles.usernameFormActions}>
                      <button type="submit" className={styles.saveButton}>Save</button>
                      <button type="button" onClick={() => setIsEditingFullName(false)} className={styles.cancelEditButton}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div className={styles.usernameDisplay}>
                    <span className={styles.value}>{getDisplayValue(profileData?.fullName, 'Not set')}</span>
                    <button onClick={() => {
                      setIsEditingFullName(true);
                      setNewFullName(profileData?.fullName || '');
                    }} className={styles.editButton}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm-1.586 3-9.5 9.5a.5.5 0 0 0-.146.354v2.5a.5.5 0 0 0 .5.5h2.5a.5.5 0 0 0 .354-.146l9.5-9.5-3.5-3.5z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Username</label>
                {isEditingUsername ? (
                  <form onSubmit={handleUsernameUpdate} className={styles.usernameForm}>
                    <input
                      type="text"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                      className={styles.usernameInput}
                      autoFocus
                    />
                    <div className={styles.usernameFormActions}>
                      <button type="submit" className={styles.saveButton}>Save</button>
                      <button type="button" onClick={() => setIsEditingUsername(false)} className={styles.cancelEditButton}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div className={styles.usernameDisplay}>
                    <span className={styles.value}>{getDisplayValue(profileData?.username)}</span>
                    <button onClick={() => {
                      setIsEditingUsername(true);
                      setNewUsername(profileData?.username || '');
                    }} className={styles.editButton}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm-1.586 3-9.5 9.5a.5.5 0 0 0-.146.354v2.5a.5.5 0 0 0 .5.5h2.5a.5.5 0 0 0 .354-.146l9.5-9.5-3.5-3.5z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Email</label>
                <span className={styles.value}>{getDisplayValue(profileData?.email)}</span>
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Role</label>
                <span className={styles.value}>
                  <span className={styles.badge}>{getDisplayValue(profileData?.role)}</span>
                </span>
              </div>
            </div>
          </div>
          )}

          {activeTab === 'account' && (
          <div id="section-account" role="tabpanel" aria-labelledby="tab-account" className={styles.section}>
            <h2 className={styles.sectionTitle}>Organisation Logo</h2>
            <OrganisationLogo />

            <h2 className={styles.sectionTitle} style={{ marginTop: '2rem' }}>Account Details</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label className={styles.label}>Region</label>
                <span className={styles.value}>{getDisplayValue(profileData?.region)}</span>
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Location</label>
                <span className={styles.value}>{getDisplayValue(profileData?.location)}</span>
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Mobile Number</label>
                <span className={styles.value}>{getDisplayValue(profileData?.mobileNumber)}</span>
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Customer ID</label>
                <span className={styles.value}>{getDisplayValue(profileData?.customerId)}</span>
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>User ID</label>
                <span className={styles.value}>{getDisplayValue(profileData?.user_id)}</span>
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Sector Name</label>
                <span className={styles.value}>{getDisplayValue(profileData?.sector?.name)}</span>
              </div>
            </div>
          </div>
          )}

          {activeTab === 'authentication' && (
          <div id="section-authentication" role="tabpanel" aria-labelledby="tab-authentication" className={styles.section}>
            <h2 className={styles.sectionTitle}>Authentication</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label className={styles.label}>Password Status</label>
                <span className={`${styles.value} ${getPasswordStatusColor()}`}>
                  {getPasswordStatus()}
                </span>
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Login Methods</label>
                <span className={styles.value}>
                  {profileData?.providers?.map((provider, index) => (
                    <span key={provider} className={styles.providerBadge}>
                      {provider}
                      {index < profileData.providers.length - 1 && ''}
                    </span>
                  )) || 'Unknown'}
                </span>
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Actions</label>
                <button
                  className={styles.passwordButton}
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                >
                  {profileData?.password ? 'Change Password' : 'Set Password'}
                </button>
              </div>
            </div>

            {showPasswordForm && (
              <div className={styles.passwordFormContainer}>
                <form onSubmit={handlePasswordSubmit} className={styles.passwordForm}>
                  {profileData?.password && (
                    <div className={styles.formGroup}>
                      <label htmlFor="currentPassword">Current Password</label>
                      <input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        required
                        placeholder="Enter your current password"
                      />
                    </div>
                  )}
                  
                  <div className={styles.formGroup}>
                    <label htmlFor="newPassword">New Password</label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      required
                      minLength={6}
                      placeholder="Enter your new password"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                      minLength={6}
                      placeholder="Confirm your new password"
                    />
                    {passwordForm.confirmPassword && (
                      <p 
                        className={passwordForm.newPassword === passwordForm.confirmPassword ? styles.passwordMatch : styles.passwordMismatch}
                      >
                        {passwordForm.newPassword === passwordForm.confirmPassword ? 'Passwords matched' : 'Passwords not matched'}
                      </p>
                    )}
                  </div>

                  <div className={styles.formActions}>
                    <button
                      type="submit"
                      className={styles.submitButton}
                      disabled={updatingPassword}
                    >
                      {updatingPassword ? 'Updating...' : (profileData?.password ? 'Update Password' : 'Set Password')}
                    </button>
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordForm({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
          )}

          {activeTab === 'activity' && (
          <div id="section-activity" role="tabpanel" aria-labelledby="tab-activity" className={styles.section}>
            <h2 className={styles.sectionTitle}>Account Activity</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label className={styles.label}>Member Since</label>
                <span className={styles.value}>{formatDate(profileData?.created_date)}</span>
              </div>
              <div className={styles.infoItem}>
                <label className={styles.label}>Last Updated</label>
                <span className={styles.value}>{formatDate(profileData?.updated_date)}</span>
              </div>
            </div>
          </div>
          )}

          {activeTab === 'billing' && (
          <div id="section-billing" role="tabpanel" aria-labelledby="tab-billing" className={styles.section}>
            <h2 className={styles.sectionTitle}>Billing</h2>
            <BillingSection />
          </div>
          )}

          {activeTab === 'activity' && profileData?.permissions && Object.keys(profileData.permissions).length > 0 && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Permissions</h2>
              <div className={styles.permissions}>
                {Object.entries(profileData.permissions).map(([key, value]) => (
                  <div key={key} className={styles.permissionItem}>
                    <span className={styles.permissionKey}>{key}:</span>
                    <span className={styles.permissionValue}>{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
          )}
      </div>
    </SidebarLayout>
  );
};

export default ProfilePage;
